/**
 * boundaryType — the `Organisation.boundary_type` vocabulary, and what the map
 * calls the org boundary.
 *
 * The backend models the type as a closed set (`Organisation.BoundaryType`) and
 * derives the boundary layer's `label` from it — "Native title boundary",
 * "Other boundary", and so on. `other` is the MODEL DEFAULT, so an org reading
 * "Other boundary" on the layers toggle is almost never one that chose "other":
 * it is one nobody ever asked. Confirmed by Prabin, 15 Sep.
 *
 * Two jobs follow from that, and this module holds both so the vocabulary is
 * written down once:
 *
 * - the org form offers the four values by their real names, and offers no
 *   pre-selected default, so new orgs stop inheriting the problem;
 * - the layers toggle renders `other` as "Organisation boundary" rather than
 *   "Other boundary". That is a presentation call, and the backend explicitly
 *   left it to us — the raw type travels on the boundary GeoJSON's feature
 *   properties for exactly this purpose.
 */

/** The backend's choices, ordered most-specific first — `other` is the catch-all. */
export const BOUNDARY_TYPES = ["native_title", "national_park", "state", "other"] as const;

export type BoundaryType = (typeof BOUNDARY_TYPES)[number];

/** `Organisation.BoundaryType` labels, verbatim from the backend model. */
export const BOUNDARY_TYPE_LABELS: Record<BoundaryType, string> = {
  native_title: "Native title",
  national_park: "National park",
  state: "State",
  other: "Other"
};

/**
 * What the boundary is called when its type says nothing useful — `other`, or a
 * type we haven't loaded yet. Never "Other boundary": that names the default
 * nobody chose, and reads on the toggle as a category of boundary rather than as
 * this organisation's own.
 */
export const ORG_BOUNDARY_GENERIC_LABEL = "Organisation boundary";

const isBoundaryType = (value: unknown): value is BoundaryType => BOUNDARY_TYPES.includes(value as BoundaryType);

/**
 * Reads `boundary_type` out of the boundary GeoJSON's feature properties — the
 * only place the raw type reaches the app, since `/api/v2/config/` carries just
 * the derived label. Accepts a FeatureCollection, a Feature, or nothing.
 *
 * Returns the raw string rather than narrowing to {@link BoundaryType}, so a
 * choice added to the backend after this file was written is reported as-is and
 * {@link boundaryLayerLabel} can defer to the label the backend sent.
 */
export const boundaryTypeOf = (geojson: unknown): string | undefined => {
  const source = geojson as { features?: Array<{ properties?: Record<string, unknown> }>; properties?: Record<string, unknown> } | null;
  if (!source || typeof source !== "object") return undefined;
  const features = Array.isArray(source.features) ? source.features : [source];
  for (const feature of features) {
    const type = feature?.properties?.boundary_type;
    if (typeof type === "string" && type) return type;
  }
  return undefined;
};

/**
 * The name to show for the org boundary on the layers toggle.
 *
 * `fallback` is the backend's own `label`, used whenever the type is unknown to
 * us: during the window before the GeoJSON has loaded, and for any choice added
 * to the backend since — in both cases the backend's label is better than a
 * guess. Only `other` is overridden, and only because the backend's label for it
 * names a default rather than a decision.
 */
export const boundaryLayerLabel = (boundaryType?: string | null, fallback?: string | null): string => {
  if (isBoundaryType(boundaryType) && boundaryType !== "other") return `${BOUNDARY_TYPE_LABELS[boundaryType]} boundary`;
  if (boundaryType === "other") return ORG_BOUNDARY_GENERIC_LABEL;
  return fallback || ORG_BOUNDARY_GENERIC_LABEL;
};
